import React, { PropsWithChildren } from 'react'
import clsx from 'clsx'
import { SideNav as SideNavStyles } from '@airbus/elements/elements'
import { createUseStyles, ColorModeProvider } from '../../lib/styling'
import { useTheme } from '../../lib/styling'
import useMediaQuery from '../../lib/hooks/useMediaQuery'
import { BreakpointName, ThemeMode } from '../helperTypes'
import Drawer, { DrawerProps } from '../Drawer/Drawer'
import SideNavTitle from './SideNavTitle'
import { CommonProps } from '../helperTypes'

const useStyles = createUseStyles(SideNavStyles, 'sidenav')

type Props = PropsWithChildren<{
  /** optional props for the Drawer component in temporary mode */
  drawerProps?: DrawerProps
  /** to move side nav at any elevation, pass z-index as value */
  elevation?: number
  /** show fullscreen until one of the breakpoint names */
  fullScreenUntil?: BreakpointName
  /** Automatically manages SideNav padding to display content below the Header */
  headerPadding?: boolean
  /** Called when the SideNav closes */
  onClose?: () => void
  /** If true, the SideNav will open */
  open?: boolean
  /** Make the SideNav permanently visible */
  permanent?: boolean
  /**
   * Theme/color that will impact children variants
   * @default "light"
   */
  theme?: ThemeMode
  /** Display a title at the top of the Side Nav. Title will be visible until
   *  breakpoint value provided at `fullScreenUntil` prop */
  title?: string
}>

export type SideNavProps = Props & CommonProps

export const SideNav = (props: SideNavProps) => {
  const {
    children,
    drawerProps,
    onClose,
    open,
    permanent,
    theme = 'light',
    title = '',
    headerPadding = false,
    elevation,
    fullScreenUntil,
    ...rest
  } = props
  const {
    responsive: { breakpoint },
  } = useTheme()

  const mqMatches = useMediaQuery(fullScreenUntil ? `(max-width: ${breakpoint[fullScreenUntil]}px)` : null)
  // since permanent, the side nav is below the header, so no need for
  // displayHeader

  if (permanent) {
    return (
      <ColorModeProvider mode={theme === 'light' ? 'default' : 'dark'}>
        <SideNavWrapper elevation={elevation} fullScreenUntil={fullScreenUntil} {...rest}>
          {children}
        </SideNavWrapper>
      </ColorModeProvider>
    )
  }
  // since, drawer component, is not in usual flow,
  return (
    <Drawer open={open} theme={theme} onClose={onClose} style={{ zIndex: elevation }} {...drawerProps}>
      <SideNavWrapper headerPadding={headerPadding} fullScreenUntil={fullScreenUntil} {...rest}>
        {mqMatches && <SideNavTitle onClose={onClose}>{title}</SideNavTitle>}
        {children}
      </SideNavWrapper>
    </Drawer>
  )
}

// separated component to ensure useStyles gets\
// correct theme context value from ColorModeProvider
const SideNavWrapper = ({ children, className, headerPadding, fullScreenUntil, elevation, ...rest }: SideNavProps) => {
  const classes = useStyles()
  const {
    responsive: { breakpoint },
  } = useTheme()
  const mqMatches = useMediaQuery(fullScreenUntil ? `(max-width: ${breakpoint[fullScreenUntil]}px)` : null)
  return (
    <nav
      className={clsx(
        classes.root,
        headerPadding && classes.headerPadding,
        fullScreenUntil && classes[`width-${fullScreenUntil}`],
        mqMatches && classes._large,
        className,
      )}
      style={{ zIndex: elevation }}
      {...rest}
    >
      {children}
    </nav>
  )
}

export default SideNav
